import React, { useState, useEffect, useRef } from 'react';
import { useSiteConfig } from '../contexts/SiteConfigContext';
import { Slide, GalleryItem, Review } from '../data/defaultConfig';
import { 
  uploadImageToStorage,
  loadSubmissionsFromSupabase,
  saveSubmissionToSupabase,
  deleteSubmissionFromSupabase,
  clearAllSubmissionsFromSupabase,
  FormSubmission
} from '../services/supabaseService';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { 
  Lock, Settings, Image as ImageIcon, Star, LogOut, Save, Download, 
  Trash2, Plus, ArrowUp, ArrowDown, Search, 
  Mail, MessageCircle, ClipboardList, LayoutDashboard, ChevronRight,
  Eye, X, Trash, Calendar, Loader2, Database, AlertCircle, CheckCircle2,
  ShieldCheck, ArrowLeft, Edit
} from 'lucide-react';

// Daftar email admin yang boleh login. Bisa lebih dari satu, dipisah koma:
//   VITE_ADMIN_EMAILS=a@x.com,b@x.com,c@x.com
// VITE_ADMIN_EMAIL (tunggal) tetap didukung untuk kompatibilitas.
const ADMIN_EMAILS: string[] = Array.from(new Set(
  `${(import.meta.env.VITE_ADMIN_EMAILS as string) || ''},${(import.meta.env.VITE_ADMIN_EMAIL as string) || ''}`
    .split(/[,;\s]+/)
    .map(e => e.trim().toLowerCase())
    .filter(Boolean)
));
const isAllowedAdminEmail = (email?: string | null): boolean =>
  !!email && ADMIN_EMAILS.includes(email.trim().toLowerCase());
const FALLBACK_PASSCODE = 'admin123';

// ── Magic Link Auth Sub-Component ──────────────────────────
const OtpLoginScreen: React.FC<{ onAuthorized: () => void }> = ({ onAuthorized }) => {
  // Step: 'email' = enter email; 'sent' = magic link sent; 'fallback' = legacy passcode
  const [step, setStep] = useState<'email' | 'sent' | 'fallback'>(
    isSupabaseConfigured && ADMIN_EMAILS.length > 0 ? 'email' : 'fallback'
  );
  const [email, setEmail] = useState('');
  const [passcode, setPasscode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Check for existing Supabase session on mount.
  // Sesi hanya diterima jika email user ada di daftar ADMIN_EMAILS.
  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) return;
    const client = supabase;
    const acceptSession = (session: { user?: { email?: string | null } } | null) => {
      if (!session) return;
      if (isAllowedAdminEmail(session.user?.email)) {
        onAuthorized();
      } else {
        client.auth.signOut();
        setStep('email');
        setError('This email is not authorized to access the admin panel.');
      }
    };
    client.auth.getSession().then(({ data }) => acceptSession(data.session));
    const { data: { subscription } } = client.auth.onAuthStateChange((_event, session) => acceptSession(session));
    return () => subscription.unsubscribe();
  }, []);

  // ── Step 1: Send Magic Link ──
  const handleSendLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedEmail) { setError('Please enter your email address.'); return; }
    if (!isAllowedAdminEmail(trimmedEmail)) {
      setError('This email is not authorized to access the admin panel.');
      return;
    }
    if (!supabase) { setError('Supabase not configured.'); return; }

    setLoading(true);
    const { error: otpError } = await supabase.auth.signInWithOtp({
      email: trimmedEmail,
      options: { 
        shouldCreateUser: true,
        redirectTo: window.location.origin + '/admin'
      }
    });
    setLoading(false);

    if (otpError) {
      setError(otpError.message);
    } else {
      setStep('sent');
    }
  };

  // ── Fallback passcode ──
  const handleFallbackLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (passcode === FALLBACK_PASSCODE) {
      sessionStorage.setItem('tncakes_admin_auth', 'true');
      onAuthorized();
    } else {
      setError('Incorrect passcode. Please try again.');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 px-4 py-12 relative overflow-hidden">
      <div className="absolute top-1/4 left-1/4 w-80 h-80 bg-rose-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#7a3255]/10 rounded-full blur-3xl pointer-events-none" />

      <div className="w-full max-w-md bg-slate-800/60 backdrop-blur-xl border border-slate-700/60 p-8 rounded-3xl shadow-2xl relative z-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-tr from-[#7a3255] to-rose-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg animate-pulse">
            {step === 'sent' ? <ShieldCheck className="w-7 h-7 text-white" /> : <Lock className="w-7 h-7 text-white" />}
          </div>
          <h1 className="text-3xl font-serif font-bold text-white">
            {step === 'sent' ? 'Check Your Inbox' : 'Admin Access'}
          </h1>
          <p className="text-slate-400 text-sm mt-2 leading-relaxed">
            {step === 'email' && 'Enter your admin email to receive a passwordless magic sign-in link.'}
            {step === 'sent' && (
              <>
                We sent a secure sign-in link to:<br />
                <strong className="text-rose-400 block mt-1 break-all">{email}</strong>
              </>
            )}
            {step === 'fallback' && 'TN Cakes Bali Control Center'}
          </p>
        </div>

        {/* ── STEP: Email Entry ── */}
        {step === 'email' && (
          <form onSubmit={handleSendLink} className="space-y-5">
            <div>
              <label className="block text-slate-300 text-xs font-semibold uppercase tracking-wider mb-2">Admin Email Address</label>
              <input
                type="email" value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="admin@yourdomain.com"
                autoComplete="email"
                className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-rose-500 text-sm"
                required
              />
            </div>
            {error && <p className="text-rose-400 text-xs font-medium flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5" />{error}</p>}
            <button type="submit" disabled={loading}
              className="w-full py-3.5 bg-gradient-to-r from-rose-500 to-[#7a3255] hover:from-rose-600 hover:to-[#8c3d64] disabled:opacity-60 text-white font-bold rounded-xl transition shadow-lg cursor-pointer text-sm tracking-wider uppercase flex items-center justify-center gap-2">
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending Link...</> : <><Mail className="w-4 h-4" /> Send Sign-In Link</>}
            </button>
          </form>
        )}

        {/* ── STEP: Link Sent ── */}
        {step === 'sent' && (
          <div className="space-y-6 text-center">
            <div className="bg-slate-900/40 border border-slate-700/40 p-5 rounded-2xl text-xs text-slate-200 leading-relaxed text-left">
              <span className="font-semibold block mb-1 text-white">Next Steps:</span>
              <ol className="list-decimal list-inside space-y-1.5 text-slate-200">
                <li>Open the email client for your inbox.</li>
                <li>Find the email from <strong>Supabase Auth</strong>.</li>
                <li>Click the <strong>"Sign In"</strong> button/link inside the email.</li>
                <li>This page will automatically reload and unlock!</li>
              </ol>
            </div>
            
            <button type="button" onClick={() => { setStep('email'); setError(''); }}
              className="flex items-center gap-1.5 text-slate-400 hover:text-slate-200 text-xs font-semibold transition cursor-pointer mx-auto">
              <ArrowLeft className="w-3.5 h-3.5" /> Use a different email
            </button>
          </div>
        )}

        {/* ── STEP: Fallback Passcode ── */}
        {step === 'fallback' && (
          <form onSubmit={handleFallbackLogin} className="space-y-5">
            <div>
              <label className="block text-slate-300 text-xs font-semibold uppercase tracking-wider mb-2">Security Passcode</label>
              <input
                id="passcode" type="password" value={passcode}
                onChange={e => setPasscode(e.target.value)}
                placeholder="Enter passcode"
                className="w-full bg-slate-900/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-rose-500 text-sm"
                required
              />
            </div>
            {error && <p className="text-rose-400 text-xs font-medium">{error}</p>}
            <button type="submit"
              className="w-full py-3.5 bg-gradient-to-r from-rose-500 to-[#7a3255] hover:from-rose-600 hover:to-[#8c3d64] text-white font-bold rounded-xl transition shadow-lg cursor-pointer text-sm tracking-wider uppercase">
              Unlock Dashboard
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

// ── Main Admin Component ──────────────────────────────────────
const Admin: React.FC = () => {
  const { config, updateConfig } = useSiteConfig();
  const [isAuthorized, setIsAuthorized] = useState(() => {
    if (isSupabaseConfigured) return false; // Supabase session checked in OtpLoginScreen
    return sessionStorage.getItem('tncakes_admin_auth') === 'true';
  });

  type TabId = 'dashboard' | 'general' | 'slider' | 'gallery' | 'reviews' | 'submissions' | 'system';
  const [activeTab, setActiveTab] = useState<TabId>('dashboard');

  // Save state
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // General info state
  const [phoneTony, setPhoneTony] = useState(config.phoneNumberTony);
  const [phoneNila, setPhoneNila] = useState(config.phoneNumberNila);
  const [msgTonyEn, setMsgTonyEn] = useState(config.whatsappMessageTonyEn);
  const [msgTonyId, setMsgTonyId] = useState(config.whatsappMessageTonyId);
  const [msgNilaEn, setMsgNilaEn] = useState(config.whatsappMessageNilaEn);
  const [msgNilaId, setMsgNilaId] = useState(config.whatsappMessageNilaId);

  // Ratings
  const [gRating, setGRating] = useState(config.googleRating);
  const [gReviewCount, setGReviewCount] = useState(config.googleReviewCount);

  // Lists
  const [slides, setSlides] = useState<Slide[]>(config.slides);
  const [reviews, setReviews] = useState<Review[]>(config.reviews);
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>(config.galleryItems);
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);

  // Selected submission detail
  const [selectedSubmission, setSelectedSubmission] = useState<FormSubmission | null>(null);

  // Gallery filters
  const [gallerySearch, setGallerySearch] = useState('');
  const [galleryCategoryFilter, setGalleryCategoryFilter] = useState('all');
  const [galleryPage, setGalleryPage] = useState(1);
  const itemsPerPage = 8;

  // Submission filters
  const [submissionSearch, setSubmissionSearch] = useState('');
  const [submissionMethodFilter, setSubmissionMethodFilter] = useState('all');

  // Modals
  const [showNewSlideModal, setShowNewSlideModal] = useState(false);
  const [showNewReviewModal, setShowNewReviewModal] = useState(false);
  const [showNewGalleryModal, setShowNewGalleryModal] = useState(false);
  const [editingReview, setEditingReview] = useState<Review | null>(null);

  // Image upload states
  const [uploadingSlideImage, setUploadingSlideImage] = useState(false);
  const [uploadingGalleryImage, setUploadingGalleryImage] = useState(false);
  const [uploadingReviewerPhoto, setUploadingReviewerPhoto] = useState(false);

  // New item forms
  const [newSlide, setNewSlide] = useState<Partial<Slide>>({
    src: '', lazy: true,
    badge: { en: '', id: '' }, title: { en: '', id: '' },
    subtitle: { en: '', id: '' }, ctaLabel: { en: '', id: '' },
    ctaLink: '', hidePrimaryCta: false
  });

  const [newReview, setNewReview] = useState<Partial<Review>>({
    displayName: '', profilePhotoUrl: '/default-profile.png',
    starRating: 5, comment: '',
    createTime: new Date().toISOString().split('T')[0]
  });

  const [newGalleryItem, setNewGalleryItem] = useState<Partial<GalleryItem>>({
    code: '', image: '', title: '', category: 'wedding', description: ''
  });

  // Sync local state when context config loads
  useEffect(() => {
    setPhoneTony(config.phoneNumberTony);
    setPhoneNila(config.phoneNumberNila);
    setMsgTonyEn(config.whatsappMessageTonyEn);
    setMsgTonyId(config.whatsappMessageTonyId);
    setMsgNilaEn(config.whatsappMessageNilaEn);
    setMsgNilaId(config.whatsappMessageNilaId);
    setGRating(config.googleRating);
    setGReviewCount(config.googleReviewCount);
    setSlides(config.slides);
    setGalleryItems(config.galleryItems);
    setReviews(config.reviews);
  }, [config]);

  // Load submissions whenever tab changes to submissions or dashboard
  useEffect(() => {
    if (activeTab === 'submissions' || activeTab === 'dashboard') {
      loadSubmissions();
    }
  }, [activeTab]);

  const loadSubmissions = async () => {
    setSubmissionsLoading(true);
    if (isSupabaseConfigured) {
      const data = await loadSubmissionsFromSupabase();
      setSubmissions(data);
    } else {
      // Fallback to localStorage
      const stored = localStorage.getItem('tncakes_form_submissions');
      if (stored) {
        try { setSubmissions(JSON.parse(stored)); } catch { setSubmissions([]); }
      }
    }
    setSubmissionsLoading(false);
  };

  const handleLogout = async () => {
    if (isSupabaseConfigured && supabase) {
      await supabase.auth.signOut();
    }
    sessionStorage.removeItem('tncakes_admin_auth');
    setIsAuthorized(false);
  };

  // Upload image to Supabase Storage OR fallback to base64
  const handleImageUpload = async (
    file: File,
    bucket: 'gallery-images' | 'slide-images',
    setLoading: (v: boolean) => void,
    callback: (url: string) => void
  ) => {
    setLoading(true);
    if (isSupabaseConfigured) {
      const url = await uploadImageToStorage(file, bucket);
      if (url) {
        callback(url);
        setLoading(false);
        return;
      }
    }
    // Fallback: base64 if Supabase not configured or failed
    const reader = new FileReader();
    reader.onloadend = () => {
      callback(reader.result as string);
      setLoading(false);
    };
    reader.readAsDataURL(file);
  };

  // Reorder slides
  const moveSlide = (index: number, direction: 'up' | 'down') => {
    const updated = [...slides];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex >= 0 && targetIndex < slides.length) {
      [updated[index], updated[targetIndex]] = [updated[targetIndex], updated[index]];
      setSlides(updated);
    }
  };

  const handleAddSlide = () => {
    if (!newSlide.src) { alert('Please upload an image or enter an image URL!'); return; }
    const slideToAdd: Slide = {
      src: newSlide.src,
      lazy: newSlide.lazy !== false,
      badge: { en: newSlide.badge?.en || 'Premium', id: newSlide.badge?.id || 'Premium' },
      title: { en: newSlide.title?.en || 'Premium Cakes', id: newSlide.title?.id || 'Kue Premium' },
      subtitle: { en: newSlide.subtitle?.en || '', id: newSlide.subtitle?.id || '' },
      ctaLabel: newSlide.ctaLabel?.en ? { en: newSlide.ctaLabel.en, id: newSlide.ctaLabel.id || '' } : undefined,
      ctaLink: newSlide.ctaLink || undefined,
      hidePrimaryCta: newSlide.hidePrimaryCta,
    };
    setSlides([...slides, slideToAdd]);
    setShowNewSlideModal(false);
    setNewSlide({ src: '', lazy: true, badge: { en: '', id: '' }, title: { en: '', id: '' }, subtitle: { en: '', id: '' }, ctaLabel: { en: '', id: '' }, ctaLink: '', hidePrimaryCta: false });
  };

  const handleDeleteSlide = (index: number) => {
    if (window.confirm('Remove this slide?')) {
      setSlides(slides.filter((_, i) => i !== index));
    }
  };

  const handleAddGalleryItem = () => {
    if (!newGalleryItem.title || !newGalleryItem.image) { alert('Cake title and image are required!'); return; }
    const cat = newGalleryItem.category || 'wedding';
    const prefix = cat === 'wedding' ? 'WED' : cat === 'birthday' ? 'BTH' : cat === 'cupcakes' ? 'CUP' : cat === 'corporate' ? 'COR' : 'CEL';
    const nextNum = galleryItems.filter(item => item.category === cat).length + 1;
    const code = newGalleryItem.code || `${prefix}-${String(nextNum).padStart(3, '0')}`;
    const itemToAdd: GalleryItem = {
      id: `${cat}-${Date.now()}`,
      code,
      image: newGalleryItem.image,
      title: newGalleryItem.title,
      category: cat,
      description: newGalleryItem.description || ''
    };
    setGalleryItems([itemToAdd, ...galleryItems]);
    setShowNewGalleryModal(false);
    setNewGalleryItem({ code: '', image: '', title: '', category: 'wedding', description: '' });
  };

  const handleDeleteGalleryItem = (id: string) => {
    if (window.confirm('Delete this cake from the gallery?')) {
      setGalleryItems(galleryItems.filter(item => item.id !== id));
    }
  };

  const handleAddReview = () => {
    if (!newReview.displayName || !newReview.comment) { alert('Reviewer name and comment are required!'); return; }
    const reviewToAdd: Review = {
      reviewId: String(Date.now()),
      displayName: newReview.displayName,
      profilePhotoUrl: newReview.profilePhotoUrl || '/default-profile.png',
      starRating: Number(newReview.starRating) || 5,
      comment: newReview.comment,
      createTime: newReview.createTime || new Date().toISOString().split('T')[0]
    };
    setReviews([reviewToAdd, ...reviews]);
    setShowNewReviewModal(false);
    setNewReview({ displayName: '', profilePhotoUrl: '/default-profile.png', starRating: 5, comment: '', createTime: new Date().toISOString().split('T')[0] });
  };

  const handleDeleteReview = (id: string) => {
    if (window.confirm('Delete this testimonial?')) {
      setReviews(reviews.filter(r => r.reviewId !== id));
    }
  };

  const handleSaveEditedReview = () => {
    if (!editingReview) return;
    if (!editingReview.displayName || !editingReview.comment) {
      alert('Reviewer name and comment are required!');
      return;
    }
    setReviews(prev => prev.map(r => r.reviewId === editingReview.reviewId ? (editingReview as Review) : r));
    setEditingReview(null);
  };

  // Submission management
  const handleDeleteSubmission = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Delete this submission record?')) return;
    if (isSupabaseConfigured) {
      await deleteSubmissionFromSupabase(id);
    } else {
      const updated = submissions.filter(s => s.id !== id);
      localStorage.setItem('tncakes_form_submissions', JSON.stringify(updated));
    }
    setSubmissions(prev => prev.filter(s => s.id !== id));
  };

  const handleClearSubmissions = async () => {
    if (!window.confirm('Clear ALL submissions? This cannot be undone.')) return;
    if (isSupabaseConfigured) {
      await clearAllSubmissionsFromSupabase();
    } else {
      localStorage.setItem('tncakes_form_submissions', '[]');
    }
    setSubmissions([]);
  };

  const handleExportCSV = () => {
    if (!submissions.length) { alert('No submissions to export.'); return; }
    const headers = ['ID','Date','Name','Email','Country','WhatsApp','Date Needed','Occasion','Theme','Servings','Method','Products','Message'];
    const rows = submissions.map(s => [
      s.id, new Date(s.submittedAt).toLocaleString(), s.fullName, s.email, s.countryCode,
      s.whatsapp, s.dateNeeded, s.occasion, s.theme, s.servings, s.method,
      `"${s.productCodes?.replace(/"/g, '""')}"`, `"${s.message?.replace(/"/g, '""')}"`
    ]);
    const csv = "data:text/csv;charset=utf-8," + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const link = document.createElement('a');
    link.setAttribute('href', encodeURI(csv));
    link.setAttribute('download', `inquiries_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  // Save all changes
  const handleSaveAndApply = async () => {
    setSaving(true);
    setSaveStatus('idle');
    try {
      await updateConfig({
        phoneNumberTony: phoneTony,
        phoneNumberNila: phoneNila,
        whatsappMessageTonyEn: msgTonyEn,
        whatsappMessageTonyId: msgTonyId,
        whatsappMessageNilaEn: msgNilaEn,
        whatsappMessageNilaId: msgNilaId,
        googleRating: Number(gRating) || 5.0,
        googleReviewCount: Number(gReviewCount) || 500,
        slides, galleryItems, reviews,
      });
      setSaveStatus('success');
    } catch {
      setSaveStatus('error');
    } finally {
      setSaving(false);
      setTimeout(() => setSaveStatus('idle'), 4000);
    }
  };

  const handleExportJSON = () => {
    const data = JSON.stringify({ phoneNumberTony: phoneTony, phoneNumberNila: phoneNila, whatsappMessageTonyEn: msgTonyEn, whatsappMessageTonyId: msgTonyId, whatsappMessageNilaEn: msgNilaEn, whatsappMessageNilaId: msgNilaId, googleRating: Number(gRating), googleReviewCount: Number(gReviewCount), slides, galleryItems, reviews }, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.download = 'siteConfig.json'; link.href = url;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
  };

  // Filtered gallery
  const filteredGalleryItems = galleryItems.filter(item =>
    (item.title.toLowerCase().includes(gallerySearch.toLowerCase()) || item.code.toLowerCase().includes(gallerySearch.toLowerCase())) &&
    (galleryCategoryFilter === 'all' || item.category === galleryCategoryFilter)
  );
  const totalPages = Math.ceil(filteredGalleryItems.length / itemsPerPage) || 1;
  const startIndex = (galleryPage - 1) * itemsPerPage;
  const paginatedGalleryItems = filteredGalleryItems.slice(startIndex, startIndex + itemsPerPage);

  // Filtered submissions
  const filteredSubmissions = submissions.filter(s =>
    (s.fullName?.toLowerCase().includes(submissionSearch.toLowerCase()) ||
     s.email?.toLowerCase().includes(submissionSearch.toLowerCase()) ||
     s.productCodes?.toLowerCase().includes(submissionSearch.toLowerCase())) &&
    (submissionMethodFilter === 'all' || s.method === submissionMethodFilter)
  );

  // Sidebar nav item builder
  const NavItem = ({ id, icon: Icon, label, badge }: { id: TabId; icon: React.ElementType; label: string; badge?: number }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-xs uppercase tracking-wider transition relative ${
        activeTab === id
          ? 'bg-rose-50 text-rose-600 border border-rose-100/50'
          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-950 border border-transparent'
      }`}
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      {label}
      {badge != null && badge > 0 && (
        <span className="absolute right-3 bg-rose-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
          {badge}
        </span>
      )}
    </button>
  );

  // ── LOGIN SCREEN ─────────────────────────────────────────
  if (!isAuthorized) {
    return <OtpLoginScreen onAuthorized={() => setIsAuthorized(true)} />;
  }

  // ── MAIN DASHBOARD ────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 flex flex-col font-sans antialiased">
      
      {/* Top Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4 sticky top-0 z-50 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-tr from-[#7a3255] to-rose-500 rounded-xl flex items-center justify-center text-white font-bold font-serif shadow-md">TN</div>
          <div>
            <h1 className="text-lg font-bold text-slate-900 tracking-tight">TN Cakes Bali Management</h1>
            <div className="flex items-center gap-2 mt-0.5">
              <p className="text-xs text-slate-500 font-medium">Content Control Center</p>
              {isSupabaseConfigured ? (
                <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded-full">
                  <Database className="w-2.5 h-2.5" /> Live DB
                </span>
              ) : (
                <span className="flex items-center gap-1 text-[10px] font-bold text-amber-600 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-full">
                  <AlertCircle className="w-2.5 h-2.5" /> Local Mode
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {saveStatus === 'success' && (
            <span className="flex items-center gap-1.5 text-emerald-600 text-xs font-bold">
              <CheckCircle2 className="w-4 h-4" /> Saved & Live!
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="flex items-center gap-1.5 text-red-500 text-xs font-bold">
              <AlertCircle className="w-4 h-4" /> Save failed
            </span>
          )}

          <button
            onClick={handleSaveAndApply} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white rounded-xl font-bold text-xs tracking-wider uppercase transition shadow-sm cursor-pointer"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving...' : 'Apply Changes'}
          </button>

          <button onClick={handleLogout} className="flex items-center gap-2 px-3.5 py-2 border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-xl font-semibold text-xs tracking-wider uppercase transition cursor-pointer">
            <LogOut className="w-4 h-4" /> Lock
          </button>
        </div>
      </header>

      {/* Main Layout */}
      <div className="flex-1 max-w-7xl w-full mx-auto px-4 md:px-6 lg:px-8 py-8 flex flex-col lg:flex-row gap-8">
        
        {/* Sidebar */}
        <aside className="w-full lg:w-64 flex-shrink-0">
          {!isSupabaseConfigured && (
            <div className="bg-amber-50 border border-amber-200 text-amber-800 text-[10px] p-3 rounded-xl mb-3 leading-relaxed">
              <strong>⚠️ Supabase not connected.</strong> Changes save to browser only. Configure <code>.env.local</code> to go live.
            </div>
          )}

          <nav className="bg-white rounded-2xl border border-slate-200/80 p-3 space-y-1 shadow-sm">
            <NavItem id="dashboard" icon={LayoutDashboard} label="Dashboard Overview" />
            <NavItem id="submissions" icon={ClipboardList} label="Form Submissions" badge={submissions.length} />
            <div className="h-px bg-slate-100 my-2" />
            <NavItem id="general" icon={Settings} label="Contact & WA Numbers" />
            <NavItem id="slider" icon={ImageIcon} label="Hero Banner Slider" />
            <NavItem id="gallery" icon={ImageIcon} label="Gallery Catalog" />
            <NavItem id="reviews" icon={Star} label="GMaps Rating & Reviews" />
            <div className="h-px bg-slate-100 my-2" />
            <NavItem id="system" icon={Download} label="Publish & Export" />
          </nav>
        </aside>

        {/* Content */}
        <main className="flex-1 bg-white rounded-2xl border border-slate-200 p-6 md:p-8 shadow-sm">

          {/* ── DASHBOARD ── */}
          {activeTab === 'dashboard' && (
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-serif font-bold text-slate-900">Dashboard Overview</h2>
                <p className="text-sm text-slate-500">Live statistics and a summary of your current site configuration.</p>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Form Submissions', value: submissions.length, sub: 'Inquiry records logged' },
                  { label: 'Gallery Cakes', value: galleryItems.length, sub: 'Active portfolio items' },
                  { label: 'Google Rating', value: `${gRating.toFixed(1)} ★`, sub: `${gReviewCount} reviews` },
                  { label: 'Hero Slides', value: slides.length, sub: 'Banner carousel items' },
                ].map(card => (
                  <div key={card.label} className="bg-slate-50 border border-slate-200/80 p-5 rounded-2xl">
                    <div className="text-slate-400 text-[10px] font-bold uppercase tracking-wider">{card.label}</div>
                    <div className="text-3xl font-bold text-slate-900 mt-2">{card.value}</div>
                    <div className="text-xs text-rose-600 font-medium mt-1">{card.sub}</div>
                  </div>
                ))}
              </div>

              {/* Recent Submissions */}
              <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                <div className="px-5 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50/50">
                  <h3 className="font-serif font-bold text-slate-900 text-sm">Recent Form Submissions</h3>
                  <button onClick={() => setActiveTab('submissions')} className="text-rose-600 text-xs font-bold flex items-center gap-1 cursor-pointer hover:text-rose-700 uppercase tracking-wider">
                    View All <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="divide-y divide-slate-100">
                  {submissionsLoading && <div className="text-center py-8 text-slate-400 text-sm flex justify-center gap-2"><Loader2 className="animate-spin w-4 h-4" /> Loading...</div>}
                  {!submissionsLoading && submissions.slice(0, 3).map(sub => (
                    <div key={sub.id} className="p-5 flex justify-between items-center hover:bg-slate-50/50 transition">
                      <div>
                        <h4 className="font-bold text-slate-900 text-sm">{sub.fullName}</h4>
                        <p className="text-xs text-slate-500 flex items-center gap-2 mt-1">
                          <span>{sub.email}</span><span>•</span>
                          <span className="font-medium text-rose-600 flex items-center gap-1 capitalize">
                            {sub.method === 'email' ? <Mail className="w-3 h-3" /> : <MessageCircle className="w-3 h-3" />}
                            Via {sub.method}
                          </span>
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-xs font-bold text-slate-800">{sub.occasion}</div>
                        <span className="text-[10px] text-slate-400">{new Date(sub.submittedAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                  {!submissionsLoading && submissions.length === 0 && (
                    <div className="text-center py-10 text-slate-400 text-sm">No inquiry submissions logged yet.</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ── FORM SUBMISSIONS ── */}
          {activeTab === 'submissions' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                <div>
                  <h2 className="text-2xl font-serif font-bold text-slate-900">Form Submissions Monitor</h2>
                  <p className="text-sm text-slate-500">Customer cake inquiries submitted via the website inquiry form.</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={handleExportCSV} className="flex items-center gap-1.5 px-3.5 py-2 border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl font-semibold text-xs uppercase tracking-wider transition cursor-pointer">
                    <Download className="w-3.5 h-3.5" /> Export CSV
                  </button>
                  <button onClick={handleClearSubmissions} className="flex items-center gap-1.5 px-3.5 py-2 border border-red-200 text-red-600 hover:bg-red-50 rounded-xl font-semibold text-xs uppercase tracking-wider transition cursor-pointer">
                    <Trash className="w-3.5 h-3.5" /> Clear All
                  </button>
                </div>
              </div>

              {isSupabaseConfigured ? (
                <div className="flex items-center gap-2 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-100 px-4 py-2.5 rounded-xl">
                  <Database className="w-4 h-4" />
                  <strong>Supabase connected.</strong> Submissions are stored in the cloud database and visible across all devices.
                </div>
              ) : (
                <div className="flex items-start gap-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 px-4 py-3 rounded-xl leading-relaxed">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span><strong>Local mode:</strong> Inquiries are sent to email/WhatsApp as usual, but submission logs are captured in this browser's local storage only. Connect Supabase to sync submissions across all devices.</span>
                </div>
              )}

              <hr className="border-slate-100" />

              <div className="flex flex-col sm:flex-row gap-4 justify-between items-stretch sm:items-center">
                <div className="relative flex-1">
                  <span className="absolute inset-y-0 left-3 flex items-center text-slate-400 pointer-events-none"><Search className="w-4 h-4" /></span>
                  <input
                    type="text" value={submissionSearch}
                    onChange={(e) => setSubmissionSearch(e.target.value)}
                    placeholder="Search by name, email, product codes..."
                    className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-rose-500 shadow-sm"
                  />
                </div>
                <div className="flex gap-2">
                  {['all', 'email', 'whatsapp'].map(m => (
                    <button key={m} onClick={() => setSubmissionMethodFilter(m)}
                      className={`px-3 py-1.5 rounded-xl text-[10px] font-bold uppercase tracking-wider border transition cursor-pointer ${submissionMethodFilter === m ? 'bg-rose-500 text-white border-rose-500' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
                      {m}
                    </button>
                  ))}
                </div>
              </div>

              <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-slate-400 font-bold uppercase tracking-wider">
                        <th className="px-5 py-3.5">Date Submitted</th>
                        <th className="px-5 py-3.5">Customer</th>
                        <th className="px-5 py-3.5">Occasion / Date Needed</th>
                        <th className="px-5 py-3.5">Product Code</th>
                        <th className="px-5 py-3.5">Method</th>
                        <th className="px-5 py-3.5 text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {submissionsLoading && (
                        <tr><td colSpan={6} className="text-center py-12 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></td></tr>
                      )}
                      {!submissionsLoading && filteredSubmissions.map(sub => (
                        <tr key={sub.id} onClick={() => setSelectedSubmission(sub)} className="hover:bg-slate-50/50 cursor-pointer transition">
                          <td className="px-5 py-4 text-slate-500 whitespace-nowrap">{new Date(sub.submittedAt).toLocaleString()}</td>
                          <td className="px-5 py-4">
                            <div className="font-bold text-slate-900">{sub.fullName}</div>
                            <div className="text-[10px] text-slate-400 mt-0.5">{sub.email}</div>
                          </td>
                          <td className="px-5 py-4">
                            <div className="font-medium text-slate-800">{sub.occasion}</div>
                            <div className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5"><Calendar className="w-3 h-3" /> {sub.dateNeeded}</div>
                          </td>
                          <td className="px-5 py-4 font-mono text-slate-600">{sub.productCodes || '—'}</td>
                          <td className="px-5 py-4">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-semibold border ${sub.method === 'email' ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-green-50 text-green-700 border-green-100'}`}>
                              {sub.method === 'email' ? <Mail className="w-2.5 h-2.5" /> : <MessageCircle className="w-2.5 h-2.5" />}
                              {sub.method}
                            </span>
                          </td>
                          <td className="px-5 py-4 text-center" onClick={e => e.stopPropagation()}>
                            <div className="flex justify-center gap-1.5">
                              <button onClick={() => setSelectedSubmission(sub)} className="p-1.5 border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-600 transition cursor-pointer" title="View Details"><Eye className="w-3.5 h-3.5" /></button>
                              <button onClick={(e) => handleDeleteSubmission(sub.id, e)} className="p-1.5 border border-red-100 rounded-lg hover:bg-red-50 text-red-600 transition cursor-pointer" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {!submissionsLoading && filteredSubmissions.length === 0 && (
                        <tr><td colSpan={6} className="text-center py-12 text-slate-400 text-sm">No matching submissions found.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ── GENERAL / CONTACT ── */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-serif font-bold text-slate-900">Contact & WhatsApp Numbers</h2>
                <p className="text-sm text-slate-500">Configure phone contacts and WhatsApp redirect message templates.</p>
              </div>
              <hr className="border-slate-100" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {[
                  { flag: '🇦🇺', name: 'Tony', label: 'English Channel Admin', phone: phoneTony, setPhone: setPhoneTony, msgEn: msgTonyEn, setMsgEn: setMsgTonyEn, msgId: msgTonyId, setMsgId: setMsgTonyId },
                  { flag: '🇮🇩', name: 'Nila', label: 'Bahasa Channel Admin', phone: phoneNila, setPhone: setPhoneNila, msgEn: msgNilaEn, setMsgEn: setMsgNilaEn, msgId: msgNilaId, setMsgId: setMsgNilaId },
                ].map(person => (
                  <div key={person.name} className="bg-slate-50 border border-slate-200 rounded-2xl p-6 space-y-4">
                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                      <span className="text-lg">{person.flag}</span> {person.name} ({person.label})
                    </h3>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">WhatsApp Number</label>
                      <input type="text" value={person.phone} onChange={e => person.setPhone(e.target.value)} className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-rose-500 shadow-sm" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Template Message (English)</label>
                      <textarea value={person.msgEn} onChange={e => person.setMsgEn(e.target.value)} rows={3} className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-rose-500 shadow-sm resize-none" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Template Message (Indonesian)</label>
                      <textarea value={person.msgId} onChange={e => person.setMsgId(e.target.value)} rows={3} className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-rose-500 shadow-sm resize-none" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── HERO SLIDER ── */}
          {activeTab === 'slider' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center flex-wrap gap-4">
                <div>
                  <h2 className="text-2xl font-serif font-bold text-slate-900">Hero Banner Slider</h2>
                  <p className="text-sm text-slate-500">Add, reorder, and remove homepage hero carousel banners.</p>
                </div>
                <button onClick={() => setShowNewSlideModal(true)} className="flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold text-xs uppercase tracking-wider transition shadow-sm cursor-pointer">
                  <Plus className="w-4 h-4" /> Add Slide
                </button>
              </div>
              <hr className="border-slate-100" />
              <div className="space-y-4">
                {slides.map((slide, index) => (
                  <div key={index} className="bg-slate-50 border border-slate-200 rounded-2xl p-4 flex flex-col md:flex-row gap-4 items-start md:items-center">
                    <div className="w-full md:w-36 h-24 bg-slate-200 rounded-xl overflow-hidden flex-shrink-0">
                      <img src={slide.src} alt={slide.title.en} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="bg-rose-100 text-rose-800 text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full">{slide.badge.en} / {slide.badge.id}</span>
                      <h4 className="text-sm font-bold text-slate-900 mt-2 leading-snug whitespace-pre-line">{slide.title.en}</h4>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-1">{slide.subtitle.en}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button disabled={index === 0} onClick={() => moveSlide(index, 'up')} className={`p-2 border border-slate-200 rounded-xl hover:bg-white text-slate-600 transition ${index === 0 ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}><ArrowUp className="w-4 h-4" /></button>
                      <button disabled={index === slides.length - 1} onClick={() => moveSlide(index, 'down')} className={`p-2 border border-slate-200 rounded-xl hover:bg-white text-slate-600 transition ${index === slides.length - 1 ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}><ArrowDown className="w-4 h-4" /></button>
                      <button onClick={() => handleDeleteSlide(index)} className="p-2 border border-red-100 text-red-600 hover:bg-red-50 rounded-xl transition cursor-pointer"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── GALLERY ── */}
          {activeTab === 'gallery' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center flex-wrap gap-4">
                <div>
                  <h2 className="text-2xl font-serif font-bold text-slate-900">Gallery Catalog</h2>
                  <p className="text-sm text-slate-500">Manage portfolio items displayed in the Gallery section ({galleryItems.length} cakes).</p>
                </div>
                <button onClick={() => setShowNewGalleryModal(true)} className="flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold text-xs uppercase tracking-wider transition shadow-sm cursor-pointer">
                  <Plus className="w-4 h-4" /> Add Cake Item
                </button>
              </div>
              <hr className="border-slate-100" />

              <div className="flex flex-col sm:flex-row gap-4 justify-between items-stretch sm:items-center">
                <div className="relative flex-1">
                  <span className="absolute inset-y-0 left-3 flex items-center text-slate-400 pointer-events-none"><Search className="w-4 h-4" /></span>
                  <input type="text" value={gallerySearch} onChange={e => { setGallerySearch(e.target.value); setGalleryPage(1); }}
                    placeholder="Search by cake name or catalog code..."
                    className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-1 focus:ring-rose-500 shadow-sm" />
                </div>
                <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
                  {['all', 'wedding', 'birthday', 'cupcakes', 'corporate', 'celebration'].map(cat => (
                    <button key={cat} onClick={() => { setGalleryCategoryFilter(cat); setGalleryPage(1); }}
                      className={`px-3 py-1.5 rounded-xl text-[10px] font-bold uppercase tracking-wider border transition cursor-pointer whitespace-nowrap ${galleryCategoryFilter === cat ? 'bg-rose-500 text-white border-rose-500' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {paginatedGalleryItems.map(item => (
                  <div key={item.id} className="border border-slate-200 rounded-2xl overflow-hidden bg-white shadow-sm group">
                    <div className="aspect-square bg-slate-100 relative overflow-hidden">
                      <img src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      <span className="absolute top-2 left-2 bg-black/60 text-white text-[9px] font-bold px-1.5 py-0.5 rounded backdrop-blur-sm">{item.code}</span>
                      <button onClick={() => handleDeleteGalleryItem(item.id)} className="absolute top-2 right-2 p-1.5 bg-red-600/90 hover:bg-red-700 text-white rounded-lg cursor-pointer"><Trash2 className="w-3 h-3" /></button>
                    </div>
                    <div className="p-3">
                      <span className="text-[9px] text-rose-600 font-bold uppercase">{item.category}</span>
                      <h4 className="text-xs font-bold text-slate-800 line-clamp-1 mt-0.5">{item.title}</h4>
                    </div>
                  </div>
                ))}
              </div>

              {filteredGalleryItems.length === 0 && <div className="text-center py-12 text-slate-400 text-sm"><ImageIcon className="w-10 h-10 mx-auto mb-2 opacity-30" /><p>No cakes match your query.</p></div>}

              <div className="flex justify-between items-center text-xs pt-4 border-t border-slate-100">
                <span className="text-slate-500">Showing {startIndex + 1}–{Math.min(startIndex + itemsPerPage, filteredGalleryItems.length)} of {filteredGalleryItems.length}</span>
                <div className="flex gap-2">
                  <button disabled={galleryPage === 1} onClick={() => setGalleryPage(p => p - 1)} className="px-3.5 py-2 border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-40 font-bold uppercase text-[10px] cursor-pointer">Prev</button>
                  <button disabled={galleryPage === totalPages} onClick={() => setGalleryPage(p => p + 1)} className="px-3.5 py-2 border border-slate-200 rounded-xl hover:bg-slate-50 disabled:opacity-40 font-bold uppercase text-[10px] cursor-pointer">Next</button>
                </div>
              </div>
            </div>
          )}

          {/* ── REVIEWS & RATINGS ── */}
          {activeTab === 'reviews' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-serif font-bold text-slate-900">Google Rating & Testimonials</h2>
                <p className="text-sm text-slate-500">Update Google Maps metrics and manage displayed customer testimonials.</p>
              </div>
              <hr className="border-slate-100" />
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Google Maps Rating Score</label>
                  <input type="number" step="0.1" min="1" max="5" value={gRating} onChange={e => setGRating(Number(e.target.value))} className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-rose-500 shadow-sm" />
                  <p className="text-[10px] text-slate-400 mt-1">Enter a score between 1.0 and 5.0.</p>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Total Review Count</label>
                  <input type="number" value={gReviewCount} onChange={e => setGReviewCount(Number(e.target.value))} className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-rose-500 shadow-sm" />
                  <p className="text-[10px] text-slate-400 mt-1">Number shown next to the star rating.</p>
                </div>
              </div>

              <div className="flex justify-between items-center">
                <h3 className="font-serif font-bold text-slate-900">Customer Testimonials</h3>
                <button onClick={() => setShowNewReviewModal(true)} className="flex items-center gap-1.5 px-3 py-1.5 border border-rose-500 text-rose-600 hover:bg-rose-50 rounded-xl font-bold text-xs uppercase tracking-wider transition cursor-pointer">
                  <Plus className="w-4 h-4" /> Add Testimonial
                </button>
              </div>

              <div className="space-y-3">
                {reviews.map(review => (
                  <div key={review.reviewId} className="bg-slate-50 border border-slate-200 rounded-2xl p-4 flex gap-4 items-start">
                    <div className="w-10 h-10 rounded-full bg-rose-100 flex-shrink-0 overflow-hidden border border-slate-200">
                      <img src={review.profilePhotoUrl} alt={review.displayName} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h4 className="text-xs font-bold text-slate-900">{review.displayName}</h4>
                        <span className="text-[9px] text-slate-400">{review.createTime}</span>
                      </div>
                      <div className="flex gap-0.5 my-1">{[...Array(5)].map((_, i) => <Star key={i} className={`w-3 h-3 ${i < review.starRating ? 'text-yellow-400 fill-current' : 'text-slate-300'}`} />)}</div>
                      <p className="text-xs text-slate-600 italic line-clamp-3">"{review.comment}"</p>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => setEditingReview({ ...review })} className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 border border-transparent rounded-xl transition cursor-pointer" title="Edit testimonial"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => handleDeleteReview(review.reviewId)} className="p-1.5 text-red-600 hover:bg-red-50 border border-transparent hover:border-red-100 rounded-xl transition cursor-pointer" title="Delete testimonial"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── SYSTEM / PUBLISH ── */}
          {activeTab === 'system' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-serif font-bold text-slate-900">Publish & Export</h2>
                <p className="text-sm text-slate-500">Export configuration or manage deployment settings.</p>
              </div>
              <hr className="border-slate-100" />

              {isSupabaseConfigured ? (
                <div className="bg-emerald-50 border border-emerald-100 p-6 rounded-2xl space-y-2">
                  <h3 className="font-bold text-sm text-emerald-800 flex items-center gap-2"><Database className="w-5 h-5" /> Supabase Connected — Live Mode Active</h3>
                  <p className="text-xs text-emerald-700 leading-relaxed">
                    Your site is connected to a live Supabase database. When you click <strong>Apply Changes</strong>, updates go live instantly for all visitors — no deployment required!
                  </p>
                </div>
              ) : (
                <div className="bg-amber-50 border border-amber-100 p-6 rounded-2xl space-y-2">
                  <h3 className="font-bold text-sm text-amber-800 flex items-center gap-2"><AlertCircle className="w-5 h-5" /> Supabase Not Configured</h3>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    Changes are saved to browser localStorage only. To make updates live for all visitors, configure your <code>.env.local</code> with Supabase credentials, or use the JSON export below.
                  </p>
                </div>
              )}

              {!isSupabaseConfigured && (
                <div className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4 shadow-sm">
                  <h3 className="font-bold text-slate-900 text-sm">Static Deployment Guide (No Supabase)</h3>
                  <ol className="list-decimal list-inside space-y-3 text-xs text-slate-600 leading-relaxed pl-1">
                    <li>Click the button below to download the updated <strong className="text-slate-800">siteConfig.json</strong>.</li>
                    <li>In your local project, place it in the <strong className="text-slate-800">public/</strong> folder.</li>
                    <li>Commit and push to your Git repository.</li>
                    <li>Netlify will automatically rebuild and deploy the new content.</li>
                  </ol>
                  <button onClick={handleExportJSON} className="flex items-center gap-2 px-5 py-3 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold text-xs uppercase tracking-wider transition shadow-sm cursor-pointer">
                    <Download className="w-4 h-4" /> Download siteConfig.json
                  </button>
                </div>
              )}

            </div>
          )}
        </main>
      </div>

      {/* ── MODAL: Submission Detail ── */}
      {selectedSubmission && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-6 md:p-8 shadow-2xl border border-slate-100 max-h-[90vh] overflow-y-auto space-y-6">
            <div className="flex justify-between items-start">
              <div>
                <span className="bg-rose-50 text-rose-700 text-[10px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full border border-rose-100">Submission Details</span>
                <h3 className="text-xl font-serif font-bold text-slate-900 mt-2">{selectedSubmission.fullName}</h3>
              </div>
              <button onClick={() => setSelectedSubmission(null)} className="p-1.5 hover:bg-slate-100 text-slate-500 rounded-xl transition cursor-pointer"><X className="w-5 h-5" /></button>
            </div>
            <hr className="border-slate-100" />
            <div className="grid sm:grid-cols-2 gap-6 text-xs">
              {[
                { label: 'Email Address', value: selectedSubmission.email },
                { label: 'WhatsApp', value: `${selectedSubmission.countryCode} ${selectedSubmission.whatsapp}` },
                { label: 'Occasion', value: selectedSubmission.occasion },
                { label: 'Date Needed', value: selectedSubmission.dateNeeded },
                { label: 'Servings', value: selectedSubmission.servings || 'Not specified' },
                { label: 'Product Code(s)', value: selectedSubmission.productCodes || 'None selected' },
              ].map(field => (
                <div key={field.label}>
                  <h4 className="text-slate-400 font-bold uppercase tracking-wider text-[9px]">{field.label}</h4>
                  <p className="text-slate-800 font-semibold text-sm mt-1">{field.value}</p>
                </div>
              ))}
            </div>
            {selectedSubmission.theme && (
              <div>
                <h4 className="text-slate-400 font-bold uppercase tracking-wider text-[9px] mb-1">Cake Theme / Style</h4>
                <p className="bg-slate-50 border border-slate-200 p-3 rounded-xl text-slate-800 text-xs">{selectedSubmission.theme}</p>
              </div>
            )}
            <div>
              <h4 className="text-slate-400 font-bold uppercase tracking-wider text-[9px] mb-1">Customer Message</h4>
              <p className="bg-slate-50 border border-slate-200 p-4 rounded-xl text-slate-800 text-xs italic leading-relaxed whitespace-pre-wrap">"{selectedSubmission.message}"</p>
            </div>
            <div className="flex justify-between items-center pt-4 border-t border-slate-100 text-[10px] text-slate-400">
              <span className={`px-2 py-0.5 rounded border font-bold uppercase ${selectedSubmission.method === 'email' ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-green-50 text-green-700 border-green-100'}`}>Via {selectedSubmission.method}</span>
              <span>Submitted: {new Date(selectedSubmission.submittedAt).toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: New Slide ── */}
      {showNewSlideModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-slate-100 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-serif font-bold text-slate-900 mb-4">Add New Hero Slide</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                  Upload Image {isSupabaseConfigured ? '(stored in Supabase Storage)' : '(base64 fallback)'}
                </label>
                <div className="flex items-center gap-2">
                  <input type="file" accept="image/*"
                    onChange={e => { if (e.target.files?.[0]) handleImageUpload(e.target.files[0], 'slide-images', setUploadingSlideImage, url => setNewSlide(s => ({ ...s, src: url }))); }}
                    className="w-full text-xs text-slate-500 file:mr-3 file:py-2 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-rose-50 file:text-rose-700 hover:file:bg-rose-100 cursor-pointer" />
                  {uploadingSlideImage && <Loader2 className="w-5 h-5 animate-spin text-rose-400 flex-shrink-0" />}
                </div>
                <div className="my-2 text-center text-[10px] text-slate-400 font-bold">— OR enter URL manually —</div>
                <input type="text" placeholder="/tncakes/BirthdayCakes/BirthdayCakes1.jpeg" value={newSlide.src}
                  onChange={e => setNewSlide(s => ({ ...s, src: e.target.value }))}
                  className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-rose-500" />
              </div>
              {newSlide.src && <div className="w-full h-32 bg-slate-100 rounded-xl overflow-hidden border"><img src={newSlide.src} className="w-full h-full object-cover" /></div>}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Badge (EN)', val: newSlide.badge?.en, set: (v: string) => setNewSlide(s => ({ ...s, badge: { en: v, id: s.badge?.id || '' } })) },
                  { label: 'Badge (ID)', val: newSlide.badge?.id, set: (v: string) => setNewSlide(s => ({ ...s, badge: { en: s.badge?.en || '', id: v } })) },
                  { label: 'Title (EN)', val: newSlide.title?.en, set: (v: string) => setNewSlide(s => ({ ...s, title: { en: v, id: s.title?.id || '' } })) },
                  { label: 'Title (ID)', val: newSlide.title?.id, set: (v: string) => setNewSlide(s => ({ ...s, title: { en: s.title?.en || '', id: v } })) },
                  { label: 'Subtitle (EN)', val: newSlide.subtitle?.en, set: (v: string) => setNewSlide(s => ({ ...s, subtitle: { en: v, id: s.subtitle?.id || '' } })) },
                  { label: 'Subtitle (ID)', val: newSlide.subtitle?.id, set: (v: string) => setNewSlide(s => ({ ...s, subtitle: { en: s.subtitle?.en || '', id: v } })) },
                ].map(field => (
                  <div key={field.label}>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">{field.label}</label>
                    <input type="text" value={field.val || ''} onChange={e => field.set(e.target.value)} className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs" />
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="hideCta" checked={!!newSlide.hidePrimaryCta} onChange={e => setNewSlide(s => ({ ...s, hidePrimaryCta: e.target.checked }))} className="rounded text-rose-500" />
                <label htmlFor="hideCta" className="text-xs text-slate-700 font-semibold cursor-pointer">Hide primary "Shop" CTA button on this slide</label>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setShowNewSlideModal(false)} className="px-4 py-2 border border-slate-200 rounded-xl font-bold text-xs uppercase hover:bg-slate-50 cursor-pointer">Cancel</button>
              <button onClick={handleAddSlide} className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold text-xs uppercase cursor-pointer">Add Slide</button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: New Gallery Item ── */}
      {showNewGalleryModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-slate-100 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-serif font-bold text-slate-900 mb-4">Add Cake to Gallery</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Category</label>
                <select value={newGalleryItem.category} onChange={e => setNewGalleryItem(s => ({ ...s, category: e.target.value }))} className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs">
                  {['wedding', 'birthday', 'cupcakes', 'corporate', 'celebration'].map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)} Cakes</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Cake Name / Title</label>
                <input type="text" placeholder="e.g. Elegant White Wedding Rose" value={newGalleryItem.title}
                  onChange={e => setNewGalleryItem(s => ({ ...s, title: e.target.value }))}
                  className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                  Photo {isSupabaseConfigured ? '(uploaded to Supabase Storage)' : ''}
                </label>
                <div className="flex items-center gap-2">
                  <input type="file" accept="image/*"
                    onChange={e => { if (e.target.files?.[0]) handleImageUpload(e.target.files[0], 'gallery-images', setUploadingGalleryImage, url => setNewGalleryItem(s => ({ ...s, image: url }))); }}
                    className="w-full text-xs text-slate-500 file:mr-3 file:py-2 file:px-3 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-rose-50 file:text-rose-700 hover:file:bg-rose-100 cursor-pointer" />
                  {uploadingGalleryImage && <Loader2 className="w-5 h-5 animate-spin text-rose-400 flex-shrink-0" />}
                </div>
                <div className="my-2 text-center text-[10px] text-slate-400 font-bold">— OR enter URL manually —</div>
                <input type="text" placeholder="/tncakes/WeddingCakes/WeddingCakes1.jpeg" value={newGalleryItem.image}
                  onChange={e => setNewGalleryItem(s => ({ ...s, image: e.target.value }))}
                  className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs" />
              </div>
              {newGalleryItem.image && <div className="w-28 h-28 mx-auto bg-slate-100 rounded-2xl overflow-hidden border"><img src={newGalleryItem.image} className="w-full h-full object-cover" /></div>}
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Catalog Code (optional — auto-generated)</label>
                <input type="text" placeholder="Leave empty to auto-generate (e.g. WED-018)" value={newGalleryItem.code}
                  onChange={e => setNewGalleryItem(s => ({ ...s, code: e.target.value }))}
                  className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs" />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setShowNewGalleryModal(false)} className="px-4 py-2 border border-slate-200 rounded-xl font-bold text-xs uppercase hover:bg-slate-50 cursor-pointer">Cancel</button>
              <button onClick={handleAddGalleryItem} className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold text-xs uppercase cursor-pointer">Add Cake</button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: New Review ── */}
      {showNewReviewModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-slate-100 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-serif font-bold text-slate-900 mb-4">Add Customer Testimonial</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Customer Name</label>
                <input type="text" placeholder="e.g. Jessica Thompson" value={newReview.displayName}
                  onChange={e => setNewReview(r => ({ ...r, displayName: e.target.value }))}
                  className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Profile Photo</label>
                <div className="flex items-center gap-3 mb-2">
                  <input type="file" accept="image/*" onChange={e => {
                    if (e.target.files?.[0]) {
                      handleImageUpload(e.target.files[0], 'gallery-images', setUploadingReviewerPhoto, (url) => {
                        setNewReview(r => ({ ...r, profilePhotoUrl: url }));
                      });
                    }
                  }} className="w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-rose-50 file:text-rose-700 hover:file:bg-rose-100 cursor-pointer" />
                  {uploadingReviewerPhoto && <Loader2 className="w-5 h-5 animate-spin text-rose-400 flex-shrink-0" />}
                </div>
                <div className="flex gap-2 items-center">
                  <input type="text" placeholder="Or enter profile photo URL manually" value={newReview.profilePhotoUrl}
                    onChange={e => setNewReview(r => ({ ...r, profilePhotoUrl: e.target.value }))}
                    className="w-full bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-[10px]" />
                  {newReview.profilePhotoUrl && (
                    <div className="w-8 h-8 rounded-full overflow-hidden border border-slate-200 flex-shrink-0">
                      <img src={newReview.profilePhotoUrl} className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Star Rating</label>
                <select value={newReview.starRating} onChange={e => setNewReview(r => ({ ...r, starRating: Number(e.target.value) }))} className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs">
                  <option value="5">⭐⭐⭐⭐⭐ (5 Stars)</option>
                  <option value="4">⭐⭐⭐⭐ (4 Stars)</option>
                  <option value="3">⭐⭐⭐ (3 Stars)</option>
                  <option value="2">⭐⭐ (2 Stars)</option>
                  <option value="1">⭐ (1 Star)</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Date</label>
                <input type="date" value={newReview.createTime} onChange={e => setNewReview(r => ({ ...r, createTime: e.target.value }))} className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Testimonial Comment</label>
                <textarea placeholder="Write the customer's review..." value={newReview.comment} onChange={e => setNewReview(r => ({ ...r, comment: e.target.value }))} rows={4} className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs resize-none" />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setShowNewReviewModal(false)} className="px-4 py-2 border border-slate-200 rounded-xl font-bold text-xs uppercase hover:bg-slate-50 cursor-pointer">Cancel</button>
              <button onClick={handleAddReview} className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold text-xs uppercase cursor-pointer">Add Testimonial</button>
            </div>
          </div>
        </div>
      )}
      {/* ── MODAL: Edit Review ── */}
      {editingReview && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-slate-100 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-serif font-bold text-slate-900 mb-4">Edit Customer Testimonial</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Customer Name</label>
                <input type="text" placeholder="e.g. Jessica Thompson" value={editingReview.displayName}
                  onChange={e => setEditingReview(r => r ? ({ ...r, displayName: e.target.value }) : null)}
                  className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Profile Photo</label>
                <div className="flex items-center gap-3 mb-2">
                  <input type="file" accept="image/*" onChange={e => {
                    if (e.target.files?.[0]) {
                      handleImageUpload(e.target.files[0], 'gallery-images', setUploadingReviewerPhoto, (url) => {
                        setEditingReview(r => r ? ({ ...r, profilePhotoUrl: url }) : null);
                      });
                    }
                  }} className="w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-rose-50 file:text-rose-700 hover:file:bg-rose-100 cursor-pointer" />
                  {uploadingReviewerPhoto && <Loader2 className="w-5 h-5 animate-spin text-rose-400 flex-shrink-0" />}
                </div>
                <div className="flex gap-2 items-center">
                  <input type="text" placeholder="Or enter profile photo URL manually" value={editingReview.profilePhotoUrl}
                    onChange={e => setEditingReview(r => r ? ({ ...r, profilePhotoUrl: e.target.value }) : null)}
                    className="w-full bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-[10px]" />
                  {editingReview.profilePhotoUrl && (
                    <div className="w-8 h-8 rounded-full overflow-hidden border border-slate-200 flex-shrink-0">
                      <img src={editingReview.profilePhotoUrl} className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Star Rating</label>
                <select value={editingReview.starRating} onChange={e => setEditingReview(r => r ? ({ ...r, starRating: Number(e.target.value) }) : null)} className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs">
                  <option value="5">⭐⭐⭐⭐⭐ (5 Stars)</option>
                  <option value="4">⭐⭐⭐⭐ (4 Stars)</option>
                  <option value="3">⭐⭐⭐ (3 Stars)</option>
                  <option value="2">⭐⭐ (2 Stars)</option>
                  <option value="1">⭐ (1 Star)</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Date</label>
                <input type="date" value={editingReview.createTime} onChange={e => setEditingReview(r => r ? ({ ...r, createTime: e.target.value }) : null)} className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Testimonial Comment</label>
                <textarea placeholder="Write the customer's review..." value={editingReview.comment} onChange={e => setEditingReview(r => r ? ({ ...r, comment: e.target.value }) : null)} rows={4} className="w-full bg-white border border-slate-200 rounded-xl px-3 py-2 text-xs resize-none" />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setEditingReview(null)} className="px-4 py-2 border border-slate-200 rounded-xl font-bold text-xs uppercase hover:bg-slate-50 cursor-pointer">Cancel</button>
              <button onClick={handleSaveEditedReview} className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl font-bold text-xs uppercase cursor-pointer">Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
